import { useEffect, useState } from "react";

import { hasPermissionOnUserManagement } from "@simplyk/common";
import { useRouter } from "next/router";

import { useCurrentUserContext } from "../../../contexts/CurrentUserContext";
import { GetCurrentUserFrontendObject } from "../../../contexts/types";

import { LiveEditorStep, LiveEditorStepKey, isValidStepKey } from "@/features/LiveFormEditor/Steps/LiveEditorStep.enum";
import { TicketingOutput, DonationFormWithUserOrOrgAccessOutput } from "@/types/trpc";

export const useStepNavigation = (
  steps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[],
  apiFormObject: TicketingOutput | DonationFormWithUserOrOrgAccessOutput
) => {
  const router = useRouter();
  const { currentUser } = useCurrentUserContext();
  const initialStepKey = getInitialStepKey(apiFormObject, currentUser);
  const [currentStepKey, setCurrentStepKey] = useState<LiveEditorStepKey>(initialStepKey);

  const liveEditorStepsKeys = steps.filter((step) => !step.shouldBeSkip).map((step) => step.key);

  const getIndexStepFromKey = (key: string): LiveEditorStep =>
    liveEditorStepsKeys.findIndex((step) => step === key) as LiveEditorStep;

  const getKeyStepFromIndex = (index: LiveEditorStep): LiveEditorStepKey =>
    liveEditorStepsKeys[index] as LiveEditorStepKey;

  const currentStepIndex = getIndexStepFromKey(currentStepKey);

  useEffect(() => {
    // Handle initial hash and browser back/forward
    const handleHashChange = () => {
      // Use window.location.hash to ensure we get the latest hash
      const hash = window.location.hash.slice(1);
      if (
        hash &&
        isValidStepKey(hash, hasPermissionOnUserManagement(currentUser?.newPermissions)) &&
        hash !== currentStepKey
      ) {
        setCurrentStepKey(hash);
      }
    };

    // Set initial hash if none exists
    if (!window.location.hash) {
      router.replace({ hash: initialStepKey });
    } else {
      handleHashChange();
    }

    router.events.on("hashChangeComplete", handleHashChange);
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      router.events.off("hashChangeComplete", handleHashChange);
    };
  }, [currentStepKey, currentUser?.newPermissions, initialStepKey, router]);

  const updateCurrentStep = (stepKey: LiveEditorStepKey) => {
    setCurrentStepKey(stepKey);
    window.location.hash = stepKey;
  };

  const goToNextStep = () => {
    const nextStepKey = getKeyStepFromIndex(currentStepIndex + 1);
    updateCurrentStep(nextStepKey);
  };

  const goToPreviousStep = () => {
    const previousStepKey = getKeyStepFromIndex(currentStepIndex - 1);
    updateCurrentStep(previousStepKey);
  };

  return {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    liveEditorStepsKeys,
  };
};

const getInitialStepKey = (
  apiFormObject: TicketingOutput | DonationFormWithUserOrOrgAccessOutput,
  currentUser: GetCurrentUserFrontendObject | null | undefined
): LiveEditorStepKey => {
  const hash = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
  if (hash && isValidStepKey(hash, hasPermissionOnUserManagement(currentUser?.newPermissions))) {
    return hash;
  }

  const lastSavedDraftStep = apiFormObject.status === "draft" ? apiFormObject.liveFormEditorStep : null;
  if (
    lastSavedDraftStep &&
    isValidStepKey(lastSavedDraftStep, hasPermissionOnUserManagement(currentUser?.newPermissions))
  ) {
    return lastSavedDraftStep;
  }

  return "title";
};
